import {
  Button,
  Center,
  Flex,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputLeftAddon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Select,
  Spacer,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Tooltip,
  Tr,
  useToast,
} from '@chakra-ui/react';
import { MdContentCopy } from 'react-icons/md';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { useMsal } from '@azure/msal-react';

interface ValidationInventory {
  workListInventoryId: number;
  trackingNo: string;
  validationStatus: string;
  validationRemarks: string;
  deliveryOption: string;
  faxNo: string;
}

interface InventoryItemConfirmModal {
  isOpen: boolean;
  onClose: () => void;
  selectedItem: any;
  setSelectedItem: any;
  setCopied: any;
}

function InventoryItemConfirmModal({
  isOpen,
  onClose,
  selectedItem,
  setSelectedItem,
  setCopied,
}: InventoryItemConfirmModal) {
  const handleInventoryStatusChange = (value: string) => {
    setInventoryStatus(value);

    const newStatus = value;
    setValidationData(prevValidationData => ({
      ...prevValidationData,

      workListInventoryId: selectedItem?.WorkListInventoryId,
      trackingNo: '',
      validationStatus: newStatus,
      validationRemarks: '',
      deliveryOption: '',
      faxNo: '',
    }));
  };

  const validationDataSkeleton = {
    workListInventoryId: selectedItem?.WorkListInventoryId,
    trackingNo: '',
    validationStatus: 'Success',
    validationRemarks: '',
    deliveryOption: '',
    faxNo: '',
  };

  const { accounts } = useMsal();
  const userEmail = accounts[0]?.username || '';

  const [singleRowData, setSingleRowData] = useState<any[]>([]);

  const [InventoryStatus, setInventoryStatus] = useState('Success');
  const [validationData, setValidationData] = useState<ValidationInventory>(
    validationDataSkeleton
  );
  const toast = useToast();

  const ValidateSelectedRow = async () => {
    try {
      const id = selectedItem?.WorkListInventoryId;
      const baseurl = process.env.REACT_APP_SERVER_BASE_URL as string;
      const tokenUrl = process.env.REACT_APP_TOKEN_URL_SIS as string;

      const tokenResponse = await axios.get(tokenUrl);
      if (tokenResponse.data.success) {
        const data = {
          workListInventoryId: id,
          trackingNo: validationData.trackingNo,
          validationStatus: validationData.validationStatus,
          validationRemarks: validationData.validationRemarks,
          deliveryOption: validationData.deliveryOption,
          faxNo: validationData.faxNo,
        };
        setCopied(true);

        const validationResponse = await axios.put(
          baseurl + '/Validation/Accounts/',
          data,
          {
            headers: {
              Pragma: 'no-cache',
              Authorization: `Bearer ${tokenResponse.data.token}`,
            },
          }
        );
        if (validationResponse.data.success) {
          setCopied(true);
          toast({
            title: 'Success ',
            position: 'top',
            description: 'Inventory Item has been successfully validated!',
            status: 'success',
            duration: 9000,
            isClosable: true,
          });
        } else {
          throw new Error('Failed to update validation data');
        }
      } else {
        throw new Error('Failed to retrieve auth token');
      }
    } catch (error) {
      console.error('Error updating validation data:', error);
    }
  };

  const unlockSelectedRow = async () => {
    try {
      const id = selectedItem?.WorkListInventoryId;
      const baseurl = process.env.REACT_APP_SERVER_BASE_URL as string;
      const tokenUrl = process.env.REACT_APP_TOKEN_URL_SIS as string;

      const tokenResponse = await axios.get(tokenUrl);
      if (tokenResponse.data.success) {
        const validationResponse = await axios.delete(
          baseurl + '/Validation/Accounts/' + id,
          {
            headers: {
              Pragma: 'no-cache',
              Authorization: `Bearer ${tokenResponse.data.token}`,
            },
          }
        );
        if (validationResponse.data.success) {
          setCopied(true);
        } else {
          throw new Error('Failed to delete validation data');
        }
      } else {
        throw new Error('Failed to retrieve auth token');
      }
    } catch (error) {
      console.error('Error deleting validation data:', error);
    }
  };

  const lockSelectecRow = async () => {
    try {
      const id = selectedItem?.WorkListInventoryId;
      const baseurl = process.env.REACT_APP_SERVER_BASE_URL as string;
      const tokenUrl = process.env.REACT_APP_TOKEN_URL_SIS as string;

      const tokenResponse = await axios.get(tokenUrl);
      if (tokenResponse.data.success) {
        const validationResponse = await axios.get(
          baseurl + '/Validation/Accounts/' + id + '/' + userEmail,
          {
            headers: {
              Pragma: 'no-cache',
              Authorization: 'Bearer ' + tokenResponse.data.token,
            },
          }
        );
        if (validationResponse.data.success) {
          setCopied(true);
          setSingleRowData(validationResponse.data.result);
          const resultDeliveryOption = (
            validationResponse.data.result as any
          )?.[0]?.deliveryoption;

          if (resultDeliveryOption === null || resultDeliveryOption === ' ') {
            setValidationData(prevValidationData => ({
              ...prevValidationData,
              deliveryOption: 'Upload',
            }));
          } else {
            setValidationData(prevValidationData => ({
              ...prevValidationData,
              deliveryOption: resultDeliveryOption,
            }));
          }
        } else {
          throw new Error('Failed to fetch validation data');
        }
      } else {
        throw new Error('Failed to retrieve auth token');
      }
    } catch (error) {
      console.error('Error fetching validation data:', error);
    }
  };

  useEffect(() => {
    lockSelectecRow();
  }, [InventoryStatus, selectedItem]);

  const handleSubmit = (event: any) => {
    event.preventDefault(); // Prevent the default form submission behavior

    if (
      validationData.validationStatus === 'Success' &&
      selectedItem?.DeliveryOption === 'Upload' &&
      validationData.trackingNo === ''
    ) {
      toast({
        title: 'Required Field',
        position: 'top',
        description: 'Tracking Number is required for successful validation!',
        status: 'error',
        duration: 9000,
        isClosable: true,
      });
    } else if (
      validationData.validationStatus === 'Failed' &&
      validationData.validationRemarks === ''
    ) {
      toast({
        title: 'Required Field',
        position: 'top',
        description: 'Note is required for failed validation!',
        status: 'error',
        duration: 9000,
        isClosable: true,
      });
    } else {
      ValidateSelectedRow();
      setValidationData(prevValidationData => ({
        ...prevValidationData,
        workListInventoryId: selectedItem?.WorkListInventoryId,
        trackingNo: '',
        validationStatus: 'Success',
        validationRemarks: '',
        deliveryOption: '',
        faxNo: '',
      }));
      onClose();
    }
  };

  return (
    <>
      <Modal
        isCentered
        isOpen={isOpen}
        onClose={() => {
          onClose();
          unlockSelectedRow();
        }}
        onOverlayClick={() => {
          setSelectedItem(null);
          setInventoryStatus('Success');
          unlockSelectedRow();
          setValidationData(prevValidationData => ({
            ...prevValidationData,
            workListInventoryId: selectedItem?.WorkListInventoryId,
            trackingNo: '',
            validationStatus: 'Success',
            validationRemarks: '',
            deliveryOption: '',
            faxNo: '',
          }));
        }}
        size="2xl"
      >
        <ModalOverlay />
        <ModalContent>
          <form onSubmit={handleSubmit}>
            <ModalHeader textAlign="center">
              Confirm Inventory Report
            </ModalHeader>
            <ModalCloseButton
              color="white"
              bg="#002677"
              onClick={() => {
                setSelectedItem(null);
                setInventoryStatus('Success');
                unlockSelectedRow();
                setValidationData(prevValidationData => ({
                  ...prevValidationData,
                  workListInventoryId: selectedItem?.WorkListInventoryId,
                  trackingNo: '',
                  validationStatus: 'Success',
                  validationRemarks: '',
                  deliveryOption: '',
                  faxNo: '',
                }));
              }}
            />
            <ModalBody>
              <Table fontSize="15px" size="sm">
                <Tbody>
                  <Tr>
                    <Td fontWeight="bold">Account No:</Td>
                    <Td>{(singleRowData as any)?.[0]?.accountno}</Td>
                    <Td fontWeight="bold">Patient Name:</Td>
                    <Td>{(singleRowData as any)?.[0]?.patientname}</Td>
                  </Tr>
                  <Tr>
                    <Td fontWeight="bold">Claim No:</Td>
                    <Td>{(singleRowData as any)?.[0]?.claimNumber}</Td>
                    <Td fontWeight="bold">Subscriber ID:</Td>
                    <Td>{(singleRowData as any)?.[0]?.subscriberid}</Td>
                  </Tr>
                  <Tr>
                    <Td fontWeight="bold">MRN:</Td>
                    <Td>{(singleRowData as any)?.[0]?.mrn}</Td>
                    <Td fontWeight="bold">Service Type:</Td>
                    <Td>{(singleRowData as any)?.[0]?.ServiceType}</Td>
                  </Tr>
                  <Tr>
                    <Td fontWeight="bold">Date of Service From:</Td>
                    <Td>
                      {(singleRowData as any)?.[0]?.dateofservicefrom
                        ? new Date(
                            (singleRowData as any)?.[0]?.dateofservicefrom
                          ).toLocaleDateString()
                        : ''}
                    </Td>

                    <Td fontWeight="bold">Code Date:</Td>
                    <Td>
                      {(singleRowData as any)?.[0]?.CodeDate
                        ? new Date(
                            (singleRowData as any)?.[0]?.CodeDate
                          ).toLocaleDateString()
                        : ''}
                    </Td>
                  </Tr>
                  <Tr>
                    <Td fontWeight="bold">Date of Service To:</Td>
                    <Td>
                      {(singleRowData as any)?.[0]?.dateofserviceto
                        ? new Date(
                            (singleRowData as any)?.[0]?.dateofserviceto
                          ).toLocaleDateString()
                        : ''}
                    </Td>

                    <Td fontWeight="bold">Code Type:</Td>
                    <Td>{(singleRowData as any)?.[0]?.codetype}</Td>
                  </Tr>
                  <Tr>
                    <Td fontWeight="bold">Patient DOB:</Td>
                    <Td>
                      {(singleRowData as any)?.[0]?.patientdob
                        ? new Date(
                            (singleRowData as any)?.[0]?.patientdob
                          ).toLocaleDateString()
                        : ''}
                    </Td>
                    <Td fontWeight="bold">Plan Type:</Td>
                    <Td>{(singleRowData as any)?.[0]?.plantype}</Td>
                  </Tr>
                  <Tr>
                    <Td fontWeight="bold">Number of Pages:</Td>
                    <Td>{(singleRowData as any)?.[0]?.noofpages}</Td>

                    <Td fontWeight="bold">Total Charges:</Td>
                    <Td>{(singleRowData as any)?.[0]?.totalcharges}</Td>
                  </Tr>
                  <Tr>
                    <Td fontWeight="bold">Delivery Option:</Td>
                    <Td>{(singleRowData as any)?.[0]?.deliveryoption}</Td>

                    <Td fontWeight="bold">Insurance Balance:</Td>
                    <Td>{(singleRowData as any)?.[0]?.insurancebalance}</Td>
                  </Tr>
                  <Tr>
                    <Td fontWeight="bold">Next Delivery Option:</Td>
                    <Td>{(singleRowData as any)?.[0]?.nextDeliveryOption}</Td>
                    <Td fontWeight="bold">Current Balance:</Td>
                    <Td>{(singleRowData as any)?.[0]?.currentAccbalance}</Td>
                  </Tr>
                  <Tr>
                    <Td fontWeight="bold">TIN:</Td>
                    <Td>{(singleRowData as any)?.[0]?.TIN}</Td>
                    <Td fontWeight="bold">Facility Code:</Td>
                    <Td>{(singleRowData as any)?.[0]?.facilitycode}</Td>
                  </Tr>

                  <Tr>
                    <Td fontWeight="bold">Health Plan Name:</Td>
                    <Td colSpan={3}>
                      {(singleRowData as any)?.[0]?.healthplanname}
                    </Td>
                  </Tr>
                  <Tr>
                    <Td fontWeight="bold">Payer Address:</Td>
                    <Td colSpan={3}>
                      {(singleRowData as any)?.[0]?.payeraddress}
                    </Td>
                  </Tr>
                  <Tr>
                    <Td fontWeight="bold">Facility Address:</Td>
                    <Td colSpan={3}>
                      {(singleRowData as any)?.[0]?.facilityaddress}
                    </Td>
                  </Tr>

                  <Tr>
                    <Td fontWeight="bold">File Path:</Td>
                    <Td colSpan={3}>
                      <Flex>
                        <Tooltip
                          hasArrow
                          label={(singleRowData as any)?.[0]?.filepath}
                          placement="top"
                          bg="gray.300"
                          color="black"
                        >
                          <Text
                            maxWidth="350px"
                            overflow="hidden"
                            textOverflow="ellipsis"
                            whiteSpace="nowrap"
                          >
                            {(singleRowData as any)?.[0]?.filepath}
                          </Text>
                        </Tooltip>
                        <MdContentCopy
                          style={{ marginLeft: '5px', cursor: 'pointer' }}
                          onClick={e => {
                            navigator.clipboard.writeText(
                              (singleRowData as any)?.[0]?.filepath
                            );

                            toast({
                              title: 'Success',
                              position: 'top',
                              description:
                                'File Path has been copied successfully !',
                              status: 'success',
                              duration: 9000,
                              isClosable: true,
                            });
                          }}
                        />
                      </Flex>
                    </Td>
                  </Tr>
                </Tbody>
              </Table>
              <Center>
                <RadioGroup
                  mt="15px"
                  defaultValue="Success"
                  onChange={value => handleInventoryStatusChange(value)}
                >
                  <Stack spacing={5} direction="row">
                    <Radio colorScheme="green" value="Success" size="lg">
                      Approve
                    </Radio>
                    <Radio colorScheme="red" value="Failed" size="lg">
                      Decline
                    </Radio>
                  </Stack>
                </RadioGroup>
              </Center>
              <Flex display="flex" flexDirection="row">
                <FormControl isRequired mt="15px">
                  <FormLabel>Send to:</FormLabel>
                  <Select
                    maxWidth="90%"
                    mr="5px"
                    value={
                      validationData.deliveryOption === ''
                        ? (singleRowData as any)?.[0]?.deliveryoption
                        : validationData.deliveryOption
                    }
                    onChange={event => {
                      const newDelivery = event.target.value;
                      setValidationData(prevValidationData => ({
                        ...prevValidationData,
                        deliveryOption: newDelivery,
                      }));
                    }}
                  >
                    <option value="Upload">Upload</option>
                    <option value="Fax">Fax</option>
                    <option value="Print">Print</option>
                    <option value="UHC Upload">UHC UPLOAD</option>
                    <option value="Availity Upload">AVAILITY UPLOAD</option>
                  </Select>
                </FormControl>
                {validationData.deliveryOption === 'Fax' ? (
                  <FormControl mt="15px">
                    <FormLabel>Phone No:</FormLabel>
                    <InputGroup>
                      <InputLeftAddon>+1</InputLeftAddon>
                      <Input
                        type="number"
                        placeholder="Phone Number"
                        onChange={event => {
                          const newFaxNo = event.target.value;
                          setValidationData(prevValidationData => ({
                            ...prevValidationData,
                            faxNo: newFaxNo,
                          }));
                        }}
                      />
                    </InputGroup>
                  </FormControl>
                ) : (
                  ''
                )}
              </Flex>
              {InventoryStatus === 'Success' &&
              validationData.deliveryOption === 'Upload' ? (
                <>
                  <FormControl isRequired mt="15px">
                    <FormLabel>Tracking Number:</FormLabel>
                    <Input
                      placeholder="Tracking Number"
                      onChange={event => {
                        const newTracking = event.target.value;
                        setValidationData(prevValidationData => ({
                          ...prevValidationData,
                          trackingNo: newTracking,
                        }));
                      }}
                    />
                  </FormControl>
                  <FormControl mt="15px">
                    <FormLabel>Note:</FormLabel>
                    <Textarea
                      placeholder="Write Some notes"
                      onChange={event => {
                        const newNote = event.target.value;
                        setValidationData(prevValidationData => ({
                          ...prevValidationData,
                          validationRemarks: newNote,
                        }));
                      }}
                    />
                  </FormControl>
                </>
              ) : InventoryStatus === 'Success' ? (
                <FormControl mt="15px">
                  <FormLabel>Note:</FormLabel>
                  <Textarea
                    placeholder="Write Some notes"
                    onChange={event => {
                      const newNote = event.target.value;
                      setValidationData(prevValidationData => ({
                        ...prevValidationData,
                        validationRemarks: newNote,
                      }));
                    }}
                  />
                </FormControl>
              ) : (
                <>
                  <FormControl isRequired mt="15px">
                    <FormLabel>Note:</FormLabel>
                    <Textarea
                      placeholder="Write Some notes"
                      onChange={event => {
                        const newNote = event.target.value;
                        setValidationData(prevValidationData => ({
                          ...prevValidationData,
                          validationRemarks: newNote,
                        }));
                      }}
                    />
                  </FormControl>
                </>
              )}
            </ModalBody>
            <ModalFooter justifyContent="center">
              <Button
                onClick={() => {
                  setSelectedItem(null);
                  setInventoryStatus('Success');
                  unlockSelectedRow();
                  setValidationData(prevValidationData => ({
                    ...prevValidationData,
                    workListInventoryId: selectedItem?.WorkListInventoryId,
                    trackingNo: '',
                    validationStatus: 'Success',
                    validationRemarks: '',
                    deliveryOption: '',
                    faxNo: '',
                  }));
                  onClose();
                }}
                bg="#002677"
                color="white"
                h="30px"
                w="fit-content"
                mr="5px"
              >
                Cancel
              </Button>
              <Spacer />
              <Button
                bg="#002677"
                color="white"
                h="30px"
                w="fit-content"
                type="submit"
              >
                Confirm
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </>
  );
}

export default InventoryItemConfirmModal;
